import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, FilterList } from '@modernloop/shared/components';
import { Loader } from '@modernloop/shared/helper-components';
import { Avatar, Box, Button, ListItemButton, TextField, Typography } from '@mui/material';

import { QueueDialogContent_QueueMembersFragment } from 'src/generated/mloop-graphql';

import AddMemberModal from './AddMemberModal';

type Props = {
  queueName: string;
  onNameChange: (name: string) => void;
  onMembersChange: (members: QueueDialogContent_QueueMembersFragment[]) => void;
  loading?: boolean;
};

type Fragments = {
  queueMembers: QueueDialogContent_QueueMembersFragment[];
};

const QueueDialogContent: FCWithFragments<Fragments, Props> = ({
  queueMembers,
  queueName,
  onMembersChange,
  onNameChange,
  loading,
}) => {
  const { t } = useTranslation();
  const [openAddMemberDialog, setOpenAddMemberDialog] = useState(false);

  const handleAddMember = (selectedEmployees: QueueDialogContent_QueueMembersFragment[]) => {
    onMembersChange(selectedEmployees ?? []);
  };

  const handleRemoveMember = (employeeId: string) => {
    const filteredEmployees = queueMembers.filter((employee) => employee.id !== employeeId);
    onMembersChange(filteredEmployees);
  };

  return (
    <>
      <Box display="flex" flexDirection="column" rowGap="20px">
        <TextField
          value={queueName}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={t('hiring-dashboard-settings-tasks:settings.tasks.queues.namePlaceholder')}
          fullWidth
        />
        <Box display="flex" flexDirection="column" rowGap="8px">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1">
              {t('hiring-dashboard-settings-tasks:settings.tasks.queues.membersCount', { count: queueMembers.length })}
            </Typography>
            <Button size="small" variant="contained" color="primary" onClick={() => setOpenAddMemberDialog(true)}>
              {t('hiring-dashboard-settings-tasks:settings.tasks.queues.addMembers')}
            </Button>
          </Box>

          {loading && <Loader loading />}

          {!!queueMembers.length && !loading && (
            <Box mx="-10px">
              <FilterList
                getOptionLabel={(option) => option.fullName || ''}
                options={queueMembers}
                getChildSize={() => 45}
                placeholderText={t('hiring-dashboard-settings-tasks:settings.tasks.queues.searchMembers')}
                getListHeight={() => Math.min(queueMembers.length * 45, 220)}
                renderOption={(props, option) => (
                  <ListItemButton {...props}>
                    <Box key={option.id} display="flex" width="100%" alignItems="center" columnGap="8px">
                      <Avatar
                        sx={{ width: 28, height: 28 }}
                        alt={t('hiring-dashboard-settings-tasks:settings.tasks.queues.avatarAlt')}
                        src={option.internalProfilePictureUrl ?? ''}
                        variant="rounded"
                      />
                      <Typography variant="body1">{option.fullName}</Typography>
                      <Box marginLeft="auto">
                        <Button
                          variant="text"
                          color="inherit"
                          size="small"
                          onClick={() => handleRemoveMember(option.id)}
                        >
                          {t('hiring-dashboard-settings-tasks:settings.tasks.queues.remove')}
                        </Button>
                      </Box>
                    </Box>
                  </ListItemButton>
                )}
                noOptionsText={
                  <Typography variant="body1">
                    {t('hiring-dashboard-settings-tasks:settings.tasks.queues.noMembersFound')}
                  </Typography>
                }
              />
            </Box>
          )}
        </Box>
      </Box>
      {openAddMemberDialog && (
        <AddMemberModal
          open={openAddMemberDialog}
          onClose={() => {
            setOpenAddMemberDialog(false);
          }}
          onSave={(selectedEmployees) => {
            handleAddMember(selectedEmployees);
          }}
          selectedEmployees={queueMembers}
        />
      )}
    </>
  );
};

QueueDialogContent.fragments = {
  queueMembers: gql`
    ${AddMemberModal.fragments.selectedEmployees}
    fragment QueueDialogContent_queueMembers on Employee {
      id
      ...AddMemberModal_selectedEmployees
    }
  `,
};

export default QueueDialogContent;
